
import { useEffect, useRef, useState } from "react";
import PaymentGateway from "@/components/campaign/PaymentGateway";
import TermsModal from "@/components/campaign/TermsModal";
import ThankYouPopup from "@/components/campaign/ThankYouPopup";

interface CampaignModalsProps {
  showPayment: boolean;
  showTerms: boolean;
  showThankYou: boolean;
  setShowPayment: (show: boolean) => void;
  setShowTerms: (show: boolean) => void;
  setShowThankYou: (show: boolean) => void;
  handleAcceptTerms: () => void;
  handlePaymentComplete: () => Promise<void>;
  handlePaymentCancel: () => void;
  isSubmitting: boolean;
  removeItem: (key: string) => void;
}

export const CampaignModals = ({
  showPayment,
  showTerms,
  showThankYou,
  setShowPayment,
  setShowTerms,
  setShowThankYou,
  handleAcceptTerms,
  handlePaymentComplete,
  handlePaymentCancel,
  isSubmitting,
  removeItem
}: CampaignModalsProps) => {
  const cancelInProgressRef = useRef(false);
  const campaignDataBackupRef = useRef<Record<string, string | null>>({});
  const [isDraft, setIsDraft] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  
  // Check if we're working with a draft
  useEffect(() => {
    const storedDraftId = localStorage.getItem('draftCampaignId');
    if (storedDraftId) {
      setIsDraft(true);
      setDraftId(storedDraftId);
      console.log("CampaignModals: Working with draft:", storedDraftId);
    }
  }, []);
  
  // Backup critical data when payment modal is shown
  useEffect(() => {
    if (showPayment) {
      // Save current step and campaign data
      const keysToBackup = [
        'currentStep', 
        'creatives', 
        'locationCreatives', 
        'mediaOwnerCreatives',
        'formData', 
        'selectedLocations', 
        'duration', 
        'devices',
        'campaignName',
        'useLocationSpecificCreatives',
        'useMediaOwnerSpecificCreatives',
        'draftCampaignId'  // NEW: Also back up the draft ID
      ];
      
      // Create backups
      keysToBackup.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) {
          // Save to our ref for restoration during cancellation
          campaignDataBackupRef.current[key] = value;
          
          // Also create a localStorage backup
          localStorage.setItem(`${key}Backup`, value);
        }
      });
      
      console.log("CampaignModals: Backed up campaign data:", campaignDataBackupRef.current);
    }
  }, [showPayment]);
  
  // Safe wrapper for handleAcceptTerms
  const safeHandleAccept = () => {
    try {
      console.log("CampaignModals: Safely calling handleAcceptTerms");
      handleAcceptTerms();
    } catch (error) {
      console.error("Error in CampaignModals.safeHandleAccept:", error);
    }
  };
  
  // Enhanced safe wrapper for payment cancellation with proper data restoration
  const safeHandleCancel = () => {
    if (cancelInProgressRef.current) {
      console.log("CampaignModals: Cancel already in progress, ignoring duplicate request");
      return;
    }
    
    try {
      console.log("CampaignModals: Beginning payment cancellation process");
      cancelInProgressRef.current = true;
      
      // Get current step to preserve it
      const currentStep = localStorage.getItem('currentStep') || '4';
      
      // Store draft information for restoration
      if (isDraft && draftId) {
        localStorage.setItem('canceledDraftId', draftId);
        localStorage.setItem('isDraftCancellation', 'true');
      }
      
      // Backup important step data to ensure it's not lost
      localStorage.setItem('preservedStep', currentStep);
      localStorage.setItem('preserveCampaignId', 'true');
      localStorage.setItem('paymentCanceled', 'true');
      localStorage.setItem('paymentCanceledAt', Date.now().toString());
      
      // First signal cancellation start to prevent race conditions
      document.dispatchEvent(new CustomEvent('payment-cancel-start', { 
        detail: { 
          timestamp: Date.now(), 
          step: currentStep,
          isDraft: isDraft,
          draftId: draftId
        }
      }));
      
      // Immediately restore all data from our backup ref
      Object.entries(campaignDataBackupRef.current).forEach(([key, value]) => {
        if (value && value !== 'null' && value !== 'undefined') {
          localStorage.setItem(key, value);
        }
      });
      
      // Call the handler
      handlePaymentCancel();
      
      // Signal full cancellation to all components
      document.dispatchEvent(new CustomEvent('payment-cancel-confirmed', { 
        detail: { 
          timestamp: Date.now(), 
          step: currentStep,
          formData: campaignDataBackupRef.current,
          isDraft: isDraft,
          draftId: draftId
        }
      }));
      
      // Multiple restoration passes to ensure data consistency
      // First pass - immediate
      Object.entries(campaignDataBackupRef.current).forEach(([key, value]) => {
        if (value && value !== 'null' && value !== 'undefined') {
          localStorage.setItem(key, value);
        }
      });
      
      // Second pass - after a delay to ensure step restoration
      setTimeout(() => {
        // Force step restoration
        localStorage.setItem('currentStep', currentStep);
        localStorage.setItem('preservedStep', currentStep);
        
        // If this is a draft cancellation, ensure the draft ID is preserved
        if (isDraft && draftId) {
          localStorage.setItem('draftCampaignId', draftId);
        }
        
        // Re-apply all data
        Object.entries(campaignDataBackupRef.current).forEach(([key, value]) => {
          if (value && value !== 'null' && value !== 'undefined') {
            localStorage.setItem(key, value);
          }
        });
        
        console.log("CampaignModals: Second pass restoration complete");
      }, 300);
      
      // Final cleanup
      setTimeout(() => {
        cancelInProgressRef.current = false;
      }, 2000);
    } catch (error) {
      console.error("Error in CampaignModals.safeHandleCancel:", error);
      cancelInProgressRef.current = false;
    }
  };
  
  return (
    <>
      {showPayment && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-50 p-4 animate-fade-in">
          <PaymentGateway
            onPaymentComplete={handlePaymentComplete}
            onCancel={safeHandleCancel}
            isProcessing={isSubmitting}
            isDraft={isDraft}
            draftId={draftId}
          />
        </div>
      )}

      {/* Terms Modal - DISABLED: Terms are now handled in payment popup */}
      {false && (
        <TermsModal 
          isOpen={showTerms}
          onOpenChange={setShowTerms}
          onAccept={safeHandleAccept}
          isSubmitting={isSubmitting}
        />
      )}
      
      <ThankYouPopup
        isOpen={showThankYou}
        onClose={() => {
          setShowThankYou(false);
          removeItem('currentCampaignId');
        }}
      />
    </>
  );
};
